import {
  Row, Button, Switch, Modal, Typography, Form, ConfigProvider, Tabs,
  Input, Radio, DatePicker, Table, Flex, Tooltip, Tag, 
} from 'antd';
import {
  CheckOutlined, CloseOutlined, WarningOutlined, DownOutlined, UpOutlined,
  QuestionCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, 
} from '@ant-design/icons';
import React, { useEffect } from "react";
import {  useState } from 'react';
import { useTranslation } from "react-i18next";
import { ValidatePhoneNumber } from "../../utility/global-util";
import PanelFrame from "../../components/panels/PanelFrame";
import FieldSet from '../../components/panels/Fieldset';
import dayjs from 'dayjs';
import { submitMaintenanceRequest } from "../../api/maintenance";
import { showApiError } from "../../utility/error/error-handler";
import {useScreenSizeBelow} from "../../utility/hooks/useScreenSize";
import { ViewNextServiceDetail } from './SummaryTabbedCanvas';
import OdoReadingField from "../../components/panels/OdoReadingField";
import AncillariesCardView from '../../sections/maintenance/AncillariesCardView';
import { ViewTestHint } from './SummaryTabbedCanvas';



const SubmitOrViewMaintenanceRequests = ({vehicle, nextService, isModalVisible, setIsModalVisible, setNavigatedFromRecalls, setNavigatedFromServices, notification, currentTab, setCurrentTab,
  maintenanceRequests, refreshPreviousMonthsRequests, resetAndCloseWindow, hasOlderMaintenanceRequests, submitProgress, isEligibleForMOT, isEligibleForRecall, setSubmitProgress,
  message, setToDate, setHasSubmittedRequest, setRefreshPage, loading, navigatedFromServices, navigatedFromRecalls, lastOdo, driver, motPreselected, setMotPreselected,
  vehicleAncillaries, currentRequest, setCurrentRequest}) => {

  const { t } = useTranslation();
  const { Text } = Typography;
  const { TextArea } = Input;
  const [submitForm] = Form.useForm();

  useEffect(() => {
    submitForm.resetFields();
    submitForm.setFieldValue(currentRequest);
  }, [submitForm, currentRequest]);

  const [isViewAncillaryRequestModalVisible, setIsViewAncillaryRequestModalVisible] = useState(false);  
  
  const requestTableColumns = (customRequestDate, customStatus, customMOTValue, customServiceValue, customRecallValue, customOtherValue, smallScreen) => {
    return (
      [
        {
            title: customRequestDate,
            dataIndex: 'createdOn',
            key: 'createdOn',
            width:'30%',
            render: (text) => <Text className="expandable-col-content" style={{ textWrap: 'nowrap' }}>{text}</Text>
        },
        {
              title: customStatus,
              dataIndex: 'status',
              key: 'status',
              width:'35%',
              align: 'center',
              render: (text) => {
                if(smallScreen){
                  let icon = <QuestionCircleOutlined style={{color:"cornflowerblue", fontSize:'24px'}}/>;
                  if(text.id === 'BOOKED')
                  {
                     icon = <CheckCircleOutlined style={{color:"forestgreen", fontSize:'24px'}}/>
                  } else if(text.id === 'NO_ACTION') 
                  {  
                     icon = <CloseCircleOutlined style={{color:"tomato", fontSize:'24px'}}/>
                  }
                  return <Tooltip title={text.description}>{icon}</Tooltip>
                }
                else
                {
                   return <Text>{text.description}</Text>
                }
             }
          },
          {
            dataIndex: 'jobItemsRequired',
            key: 'jobItemsRequired',
            align:'center',
            width:'30%',
            render: (jobItemsRequired) => {
              return (
                <Flex gap="4px 0" wrap>
                  {
                    jobItemsRequired?.map((jobItemRequested) => {
                      let color = 'purple';
                      let translatableValue = null;
                      
                      if (jobItemRequested === 'MOT') {
                        color = 'blue';
                        translatableValue = customMOTValue;
                      } 
                      if (jobItemRequested === 'SERVICE') {
                        color = 'purple';
                        translatableValue = customServiceValue;
                      } 
                      if (jobItemRequested === 'RECALL') {
                        color = 'volcano';
                        translatableValue = customRecallValue;
                      } 
                      if (jobItemRequested === 'OTHER') {
                        color = 'green';
                        translatableValue = customOtherValue;
                      } 
        
                      return (
                        <Tag color={color} key={jobItemRequested}>
                          {translatableValue}
                        </Tag>
                      )
                    })
                    }
                </Flex>
              );
            }      
          },      
      ]
    )
  }

  function SummaryTable({requests, customRequestDate, customStatus, customMOTValue, customServiceValue, customRecallValue, customOtherValue, hasOlderMaintenanceRequests}) {
    const smallScreen = useScreenSizeBelow(756);
    const { t } = useTranslation();
  
    const customizeRenderEmpty = () => (
      <div style={{textAlign: 'center',}}>
        <WarningOutlined style={{fontSize: 40,}}/>
        <p> <Text> &nbsp; {t('message.no_current_maintenance_requests')}</Text> </p>
      </div>
    );
  
    return (
            <ConfigProvider renderEmpty={customizeRenderEmpty}>
              <Table className="dp-expandable-col"
                     rowKey={ (record) => record.id}
                     pagination= { false }
                     columns={requestTableColumns(customRequestDate, customStatus, customMOTValue, customServiceValue, customRecallValue, customOtherValue, smallScreen)} 
                     dataSource={requests} 
                     expandIconAsCell={false}
                     expandable={
                                  {
                                    expandedRowRender: (record) => (<p style={{margin: 0,}}>{record.workRequired}</p>),
                                    expandIcon: ({ expanded, onExpand, record }) =>
                                                expanded 
                                                ? 
                                                <Row type="flex" align="left" className="dp-expandable-icon">
                                                  <UpOutlined onClick={e => onExpand(record, e)} />
                                                </Row>
                                                : 
                                                <Row type="flex" align="middle" className="dp-expandable-icon"> 
                                                  <DownOutlined className="dp-expandable-icon" onClick={e => onExpand(record, e)} />
                                                </Row>
                                  }}>
              </Table>
            </ConfigProvider>
           );
  }

  const ViewRequestTab = ({requests, hasOlderMaintenanceRequests}) => {
    return (
             <div>
               <PanelFrame title=''
                           tabMode={true} 
                           borderRadius={'5px'} 
                           backgroundType="default"
               >
                 <div>
                    <SummaryTable requests={requests} 
                             customRequestDate={t('field.request_date')}
                             customStatus={t('field.status')}
                             customMOTValue={t('value.mot')}
                             customServiceValue={t('value.service')}
                             customRecallValue={t('value.recall')}
                             customOtherValue={t('value.other')}
                             hasOlderMaintenanceRequests={hasOlderMaintenanceRequests}
                  />
                 </div>
               </PanelFrame>
             </div>
           )
  }

  const onFinish = (values) => {
    let hasRepairRequirements = values != null ? (isEligibleForMOT && values.motRequired) || (isEligibleForRecall && values.recallRequired) ||
                                values.serviceRequired || values.workRequired != null : true;

    notification.destroy();

    if (!hasRepairRequirements) {
      notification.open({
        icon: <WarningOutlined style={{color: 'var(--dp-status-colour-warning)'}}/>,
        description: t('message.missing_repair_options', {0: t('value.mot')}),
        duration: null,
    });        
    }

    

    if (hasRepairRequirements) {
      // const newMaintReq = {
      currentRequest = {
        ...currentRequest,
        ...values,
        contactPhone: values.contactNumber,
        isMotRequired: values.motRequired,
        isServiceRequired: values.serviceRequired,
        isCollectionAndDeliveryRequired: values.collectionDeliveryRequired,
        isRecallRequired: values.recallRequired,
        latestOdoReading: values.newOdo
       };
       delete currentRequest.popupAncillary;

       submitMaintenanceRequest(vehicle.vehicleId, currentRequest, setSubmitProgress)
             .then(res =>{
                message.success(t('message.successfully_submitted', {0: t('field.booking_request')}));
                setIsModalVisible(false);
                setNavigatedFromRecalls(false);
                notification.destroy();
                setToDate(dayjs().add(5, 'minute'));
                setHasSubmittedRequest(true);
                setRefreshPage(true);
                setNavigatedFromServices(false);
                setMotPreselected(false);
                setCurrentRequest({});  // resets the ancillary object 
          })
          .catch(err => showApiError(t('title.new_booking_request'), err, notification));
    }
  }

  const timeOfDayOptions = (customActionAm, customActionPm, customActionAnytime) => {
    return (
             [
              {
                label: customActionAm,
                value: 'AM',
              },
              {
                label: customActionPm,
                value: 'PM',
              },
              {
                label: customActionAnytime,
                value: 'Any Time',
              },
            ]
          )
  }

  const disabledDate = (current) => {
    // Can not select days before today + 7 days
    const dateIn7days = new Date();
    dateIn7days.setDate(dateIn7days.getDate() + 7);
  
    return current && current < dayjs(dateIn7days).endOf('day');
  };

  const EntryForm = ({onFinish, loading, isEligibleForMOT, navigatedFromServices, isEligibleForRecall, navigatedFromRecalls, lastOdo, driver}) => {

    let customFieldMot = t('field.mot_due_date');
    const motduedate =[  {
            key: '1',
            label: customFieldMot,
            value: vehicle != null && vehicle.motExpiryDate != null ? vehicle.motExpiryDate : null,
        }];
    return (
      <Form form={submitForm}
            id="maintenanceRequest"
            layout="vertical"
            onFinish={onFinish}
      >
        {vehicle && vehicle.motExpiryDate ? <FieldSet items={motduedate}></FieldSet> : <></>}
        {/* NOTE: Adding a row / col within the Form.Item causes the behaviour of the switch to change . Also, the submit does not work as expected */}
        {
          isEligibleForMOT
          ?
            <Form.Item name="motRequired" 
                       label={t('field.mot_required')}
                       layout='horizontal'
                       style={{ marginTop: '10px', marginBottom: '15px' }}
                       valuePropName="checked" 
                       initialValue={motPreselected ? motPreselected : false}
            >
              <Switch disabled={ !isEligibleForMOT }
                      checkedChildren={ <CheckOutlined/> }
                      unCheckedChildren={ <CloseOutlined/> }
              />
            </Form.Item>           
          :
            <Form.Item name="motRequired" 
                       label={t('field.mot_required')}
                       layout='horizontal'
                       style={{ marginTop: '10px', marginBottom: '15px' }}
                       valuePropName="checked" 
            >
              <Row align="middle" style={{ marginTop: '-5px' }}>
                <Switch disabled={ !isEligibleForMOT }
                        checkedChildren={ <CheckOutlined/> }
                        unCheckedChildren={ <CloseOutlined/> }
                />
                  <ViewTestHint message={ t('message.test_not_allowed')} placement="topRight" isEligibleForTest={isEligibleForMOT}/>
                </Row>
            </Form.Item>               
        }
        
        <ViewNextServiceDetail nextService={nextService}></ViewNextServiceDetail>
        <Form.Item 
          name="serviceRequired" 
          label={t('field.service_required')}
          layout='horizontal'
          valuePropName="checked" 
          initialValue={navigatedFromServices}
          >
          <Switch
            checkedChildren={ <CheckOutlined/> }
            unCheckedChildren={ <CloseOutlined/> }/>
        </Form.Item> 
  
        <Form.Item 
          name="recallRequired" 
          label={t('field.recall_required')}
          layout='horizontal'
          valuePropName="checked" 
          hidden={!isEligibleForRecall}
          initialValue={isEligibleForRecall && navigatedFromRecalls}
          >
          <Switch
            disabled={ !isEligibleForRecall }
            checkedChildren={ <CheckOutlined/> }
            unCheckedChildren={ <CloseOutlined/> }
            />
        </Form.Item> 
  
        <Form.Item 
          name="workRequired" 
          label={t('field.work_required')}
          >
          <TextArea
            placeholder={t('action.enter_value')}
            rows={4}
            maxLength={500}
            showCount
            style={{ marginBottom: '10px' }}
            />
        </Form.Item>

        <OdoReadingField
            name={'newOdo'}
            required={true}
            vehicle={vehicle}
            width="200px"
        />

        {
          vehicleAncillaries && vehicleAncillaries.length !== 0
          ?
          <AncillariesCardView vehicle={vehicle}
                               vehicleAncillaries={vehicleAncillaries}
                               currentRequest={currentRequest}
                               setCurrentRequest={setCurrentRequest} 
                               isViewAncillaryRequestModalVisible={isViewAncillaryRequestModalVisible}  
                               setIsViewAncillaryRequestModalVisible={setIsViewAncillaryRequestModalVisible}
          >
          </AncillariesCardView>
          :
          <></>
        }

        <Form.Item
          name="contactNumber" 
          label={t('field.contact_number')}
          initialValue={driver.mobilePhone}
          rules={[
            { required: true, message: t('error.no_value_named', {0: t('field.contact_number')}) },
            () => ({
              validator(_, value) {
                return ValidatePhoneNumber(value, t);
              },
          }),
          ]}>
          <Input
              allowClear={true}
              type="tel"
              maxLength={50}
              placeholder={t('action.enter_value')} />
        </Form.Item>
  
        <Form.Item 
          name="preferredDate" 
          label={t('field.preferred_date')}
          rules={[
            { required: true, message: t('error.no_value_named', {0: t('field.preferred_date') }) }]}>
          <DatePicker 
            format={t('pattern.date_only').toUpperCase()}
            placeholder={t('action.enter_value')}
            disabledDate={disabledDate}
            onChange={(date, dateString) => console.log(date, dateString)}/>
        </Form.Item>
  
        <Form.Item 
          name="preferredTimeOfDay" 
          label={t('field.preferred_time')}
          rules={[
            { required: true, message: t('error.no_value_named', {0: t('field.preferred_time') }) }]}>
          <Radio.Group 
            options={timeOfDayOptions(t('action.time_of_day_morning'), t('action.time_of_day_afternoon'), t('action.time_of_day_anytime'))}
            optionType="button"
            buttonStyle="solid">
          </Radio.Group>
        </Form.Item> 
  
        <Form.Item 
          name="collectionDeliveryRequired" 
          label={t('field.collection_delivery_required')}
          layout='horizontal'
          >
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}/>
        </Form.Item> 
  
      </Form>
    )
  }

  const ViewRequestButtons = ({maintenanceRequests, refreshPreviousMonthsRequests, resetAndCloseWindow, hasOlderMaintenanceRequests}) => {
    return (
             (maintenanceRequests?.[0]?.hasMore || hasOlderMaintenanceRequests)
             ?
             [
               <Button onClick={() => { 
                         refreshPreviousMonthsRequests();
                       }}
               >   
                 {t('action.earlier_requests')} 
               </Button>,
               <Button type="primary" 
                       onClick={() => { 
                        resetAndCloseWindow(); 
                       }}
               > 
                 {t('action.close')} 
               </Button>
             ]
             :
             [
               <Button type="primary" 
                       onClick={() => { 
                        resetAndCloseWindow(); 
                       }}
               > 
                 {t('action.close')} 
               </Button>
             ]
           )
  } 

  const RequestTab = () => {
    return (
             <div>
              <PanelFrame title=''
                          tabMode={true} 
                          borderRadius={'5px'} 
                          backgroundType="default"
              >
                <div>
                  <EntryForm onFinish={onFinish}
                             loading={loading} 
                             isEligibleForMOT={isEligibleForMOT}
                             navigatedFromServices={navigatedFromServices}
                             isEligibleForRecall={isEligibleForRecall}
                             navigatedFromRecalls={navigatedFromRecalls}
                             lastOdo={lastOdo}
                             driver={driver}
                  />
                </div>
              </PanelFrame>
             </div>
           )
  }

  const CreateRequestButtons = ({setIsModalVisible, setNavigatedFromRecalls, setNavigatedFromServices, notification, submitProgress}) => {
    return (
             [
               <Button key="cancel" 
                       onClick={(e) => {
                        setIsModalVisible(false); 
                        setNavigatedFromRecalls(false);
                        setNavigatedFromServices(false);
                        setMotPreselected(false);
                        notification.destroy();
                       }}
               >
                {t('action.cancel')}
               </Button>,
               <Button type="primary" 
                       form="maintenanceRequest" 
                       key="submit" 
                       htmlType="submit" 
                       loading={submitProgress < 100}
               >
                {t('action.submit')}
               </Button>,
             ]
           )
  }

  const deriveRequestBookingTabs = (existingRequests, hasOlderMaintenanceRequests) => {
    let requestBookingTabs = [
                               {
                                 key: 'createrequest',
                                 label: t('title.request_booking'),
                                 children: <RequestTab vehicle={vehicle} 
                                                      nextService={nextService}
                                           />,
                               }
                             ];

    let viewRequest = [
                       {
                        key: 'viewrequest',
                        label: t('title.view_maintenance_requests'),
                        children: <ViewRequestTab requests={existingRequests} 
                                                  hasOlderMaintenanceRequests={hasOlderMaintenanceRequests}
                                  />,
                       }
                      ];
  
    if (existingRequests?.length > 0 || hasOlderMaintenanceRequests)
    {
      requestBookingTabs = requestBookingTabs.concat(viewRequest);
    }
  
    return requestBookingTabs;
  }  

return (
          <Modal className="std-portal-popup dp-wrap-card-title"
                 open={isModalVisible}
                 title={t('title.booking_requests')}
                 closable={true}
                 maskClosable={false}
                 onCancel={() => {
                    setIsModalVisible(false); 
                    setNavigatedFromRecalls(false);
                    setNavigatedFromServices(false);
                    setMotPreselected(false);
                    notification.destroy();}}
                 afterClose={ () => {
                   setCurrentTab('createrequest');
                 }}
                 keyboard={false}
                 footer={
                          currentTab === 'viewrequest'
                          ?
                          <ViewRequestButtons maintenanceRequests={maintenanceRequests}
                                              refreshPreviousMonthsRequests={refreshPreviousMonthsRequests}
                                              resetAndCloseWindow={resetAndCloseWindow}
                                              hasOlderMaintenanceRequests={hasOlderMaintenanceRequests}
                          > 
                          </ViewRequestButtons> 
                          :
                          <CreateRequestButtons setIsModalVisible={setIsModalVisible} 
                                                setNavigatedFromRecalls={setNavigatedFromRecalls} 
                                                setNavigatedFromServices={setNavigatedFromServices}
                                                notification={notification}
                                                submitProgress={submitProgress}
                          >
                          </CreateRequestButtons>
                        }
          >
            <ConfigProvider
                          theme={{
                               components: {
                                            Tabs: {
                                                   /* design tokens */
                                                   horizontalItemGutter: 10,
                                                   horizontalMargin: '-15px 0px -15px 0'
                                                  },
                                           },
                            }}  //ant-tabs-nav
            >
              <Tabs activeKey={ currentTab }
                    style={{ width: '100%', minHeight: '100px', marginBottom: 2, }}
                    onChange={ (key) => {
                      setCurrentTab(key);
                    }}
                    type="line"
                    size="medium"
                    items={ deriveRequestBookingTabs(maintenanceRequests, hasOlderMaintenanceRequests)} 
              />
            </ConfigProvider>
          </Modal>  
       );
}

export default SubmitOrViewMaintenanceRequests;